// DocumentMarkerManager.js
// verwaltet alle Marker, die Dokument-Verortungen darstellen, inklusive Cluster-Handling

// Notiz-Append-Start
// Code-Richtlinie: Umgang mit optionalen Methoden wie marker.setActiveState
// ------------------------------------------------------------
// Verwende den optional chaining operator (?.()) nur dann, wenn stille Toleranz erwünscht ist.
// Beispiel: marker.setActiveState?.(true)
//
// Wenn du sicherstellen willst, dass Methoden existieren oder Fehler sichtbar werden:
// if (typeof marker.setActiveState === 'function') {
//     marker.setActiveState(true);
// } else if (import.meta.env.DEV) {
//     console.warn('Missing setActiveState handler for marker', marker);
// }
// Alternativ: publish Dev-Warnung über mediator:
// mediator.publish('marker:missingActiveHandler', { marker });
// ------------------------------------------------------------
// Notiz-Append-Ende

import { mediator } from "../../../mediator/mediatorInstance.js";

import { MainMapMarkerManager } from './MainMapMarkerManager.js'

export class DocumentMarkerManager extends MainMapMarkerManager {
    markerFactory;
    #markerClusterer;

    constructor(mapAdapter, markerFactory) {
        super(mapAdapter);
        this.markerFactory = markerFactory;
    }

    setMarkerClusterer(markerClusterer) {
        this.#markerClusterer = markerClusterer;
    }

    addMarkers(markerDataArray) {
        this.clearMarkersByType('document');

        for (const data of markerDataArray) {
            const marker = this.markerFactory.createDocumentMarker(this.mapAdapter.getMapInstance(), data);

            marker.setActiveState = function(isActive) {
                if (!marker.content) return;
                const markerContent = marker.content;
                markerContent.classList.toggle('hovered', isActive);
                if (isActive) {
                    markerContent.style.backgroundPosition = `-${parseInt(markerContent.dataset.x) + 27}px -${markerContent.dataset.y}px`;
                } else {
                    markerContent.style.backgroundPosition = `-${parseInt(markerContent.dataset.x)}px -${markerContent.dataset.y}px`;
                }
            };

            marker.customId = data[0]; // eindeutige ID z.B. Dokument-ID
            this._registerMarkerEvents(marker, data);

            this.addMarker(
                marker,
                data[0],                 // ID
                'document',              // Marker-Typ
                [data[0]],               // docIds als Array
                marker.position || null  // Position, falls vorhanden
            );
        }
        this._createMarkerClusterer();
        window.ModuleJS.Mediator.publish('map:fit:main', {});
    }

    rotateMarkers() {
        const markers = this.getMarkersByType('document');
        for(const marker of markers) {
            this.markerFactory.setRotatedState(marker.marker);
        }
    }

    _createMarkerClusterer() {
        if(this.#markerClusterer) {
            this.#markerClusterer.addMarkers(this.getMarkersByType('document').map(entry => entry.marker));
        }
    }

    _registerMarkerEvents(marker, data) {
        marker.addListener('gmp-click', () => {
            mediator.publish('marker:document:click', { marker, data });
        });

        if(marker.content) {
            marker.content.addEventListener('mouseover', (event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                mediator.publish('marker:document:mouseover', { marker, data, rect });
            });
            marker.content.addEventListener('mouseout', () => {
                mediator.publish('marker:document:mouseout', { marker, data });
            });
        }
    }

    clearMarkers() {
        if(this.#markerClusterer){
            this.#markerClusterer.clearMarkers();
        }
        super.clearAllMarkers();
    }

    highlightMarkerById(id, active = true) {
        const entries = this.getMarkerById(id);
        if (!entries) return;

        const markerList = Array.isArray(entries) ? entries : [entries]; // fallback
        for (const entry of markerList) {
            const marker = entry.marker;
            if (typeof marker.setActiveState === 'function') {
                marker.setActiveState(active);
            } else if (import.meta.env.DEV) {
                console.warn('Missing setActiveState handler for marker', marker);
            }
        }
    }

    highlightClusterContainingDocument(docId, active = true) {
        const clusterEntries = this.getMarkersByType('cluster');

        for(const entry of clusterEntries) {
            const marker = entry.marker;
            const docIds = entry.docIds;

            if(docIds.includes(docId)) {
                if (marker.content) {
                    marker.content.classList.toggle('hovered', active);
                }

                // Optional: Sprite-Shift (z.B. von x=324 auf x=351)
                marker.content.style.backgroundPosition = active
                    ? `-${324 + 27}px -0px`
                    : `-${324}px -0px`;
            }
        }
    }

    highlightMarkerContainingDocument(documentId, active = true) {
        this.highlightMarkerById(documentId, active);
        this.highlightClusterContainingDocument(documentId, active);
    }

    /**
     * Callback-Funktion für das Clusterer-Rendering
     * diese Methode wird in der Clusterer-Konfiguration als `render` übergeben.
     */
    renderCustomClusterMarker = ({ markers, _position }) => {
        const mapInstance = this.mapAdapter.getMapInstance();
        const clusterMarker = this.markerFactory.createDocumentClusterMarker(mapInstance, _position, markers.length);

        const docIds = markers.map(m => m.customId);
        const markerId = `cluster_${_position.lat()}_${_position.lng()}`;

        if (clusterMarker.content) {
            clusterMarker.content.dataset.docIds = docIds.join(',');
            clusterMarker.content.addEventListener('mouseover', (event) => {
                mediator.publish('marker:cluster:mouseover', {
                    clusterMarker,
                    event
                });
            });
            clusterMarker.content.addEventListener('mouseout', () => {
                mediator.publish('marker:cluster:mouseout', {
                    clusterMarker,
                    docIds
                });
            });
        }

        this.addMarker(clusterMarker, markerId, 'cluster', docIds, _position);
        return clusterMarker;
    };

    getAllVisibleMarkers() {
        return [
            ...this.getMarkersByType('document').map(entry => entry.marker),
            ...this.getMarkersByType('cluster').map(entry => entry.marker)
        ];
    }
}